import React, { useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportDialog({ open, onOpenChange, endpoint, templateUrl, entityLabel = "data", onDone, testIdPrefix = "import" }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    const ok = /\.(csv|xlsx|xls)$/i.test(f.name);
    if (!ok) { toast.error("Format harus .csv / .xlsx / .xls"); return; }
    setFile(f); setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.imported} ${entityLabel} berhasil diimpor`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengimpor file");
    } finally { setLoading(false); }
  };

  const reset = () => { setFile(null); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ onOpenChange(v); if(!v){ reset(); } }}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader><DialogTitle>Import {entityLabel} (CSV / Excel)</DialogTitle></DialogHeader>

        <div className="text-sm text-slate-500 -mt-1 mb-2">
          Unggah file dengan header kolom. <a href={`${API}${templateUrl}`} target="_blank" rel="noreferrer" className="text-emerald-800 hover:underline inline-flex items-center gap-1" data-testid={`${testIdPrefix}-template`}><Download className="w-3.5 h-3.5"/>Unduh template CSV</a>
        </div>

        {!result && (
          <div
            onDragOver={(e)=>{e.preventDefault(); setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={(e)=>{e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]);}}
            onClick={()=>inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:bg-slate-50"}`}
            data-testid={`${testIdPrefix}-dropzone`}
          >
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={(e)=>pick(e.target.files?.[0])} data-testid={`${testIdPrefix}-file-input`}/>
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="w-10 h-10 text-emerald-700"/>
                <div className="font-medium text-slate-800">{file.name}</div>
                <div className="text-xs text-slate-500">{(file.size/1024).toFixed(1)} KB · klik untuk ganti</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadCloud className="w-10 h-10 text-slate-400"/>
                <div className="font-medium text-slate-800">Tarik & lepas file di sini</div>
                <div className="text-xs text-slate-500">atau klik untuk memilih · .csv, .xlsx, .xls</div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-xl border p-4 space-y-3" data-testid={`${testIdPrefix}-result`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700"><CheckCircle2 className="w-5 h-5"/></div>
              <div>
                <div className="font-semibold text-slate-800">Impor selesai</div>
                <div className="text-xs text-slate-500">{result.imported} berhasil · {result.skipped} dilewati</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                <div className="flex items-center gap-1 font-medium mb-1"><AlertTriangle className="w-3.5 h-3.5"/> {result.errors.length} error</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {result.errors.slice(0,5).map((e,i)=><li key={i}>Baris {e.row}: {e.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" onClick={reset}>Impor lagi</Button>
              <Button className="brand-btn" onClick={()=>onOpenChange(false)} data-testid={`${testIdPrefix}-close`}>Selesai</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={()=>onOpenChange(false)}>Batal</Button>
              <Button className="brand-btn" onClick={submit} disabled={!file || loading} data-testid={`${testIdPrefix}-submit`}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Impor Sekarang"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
